// src/pages/use-cases/UseCasePage.js
//
// Shared layout for use-case landing pages (creators, media, edtech).
// Data-driven so the three specific pages just pass in their content.
//
// Structure:
//   - Breadcrumb (Home / Use Cases / <name>)
//   - Hero: eyebrow, headline, subhead, CTA
//   - "The problem" — narrative statement
//   - Feature grid — 4 cards explaining how DubLabs specifically helps
//   - Scenarios — 2-3 concrete use-case examples
//   - FAQ — 5 questions targeting long-tail search queries
//   - Final CTA

import React, { useRef } from 'react';
import { Box, Typography, Button, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { motion, useInView } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import useSEO from '../../hooks/useSEO';
import Breadcrumbs from '../../components/common/Breadcrumbs';

// ─── Section heading ──────────────────────────────────────────────────────
function SectionHeading({ eyebrow, title, subtitle }) {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.4, once: true });
  return (
    <Box ref={ref} sx={{ textAlign: 'center', mb: { xs: 6, md: 8 }, maxWidth: 780, mx: 'auto', px: 2 }}>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      >
        {eyebrow && (
          <Typography sx={{
            fontFamily: 'Inter', fontWeight: 500, fontSize: '0.68rem',
            letterSpacing: '0.24em', textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.35)', mb: 2.5,
          }}>
            {eyebrow}
          </Typography>
        )}
        <Typography component="h2" sx={{
          fontFamily: 'Inter', fontWeight: 900,
          fontSize: 'clamp(1.8rem, 4vw, 3rem)',
          color: '#fff', lineHeight: 1.05, letterSpacing: '-0.03em', mb: subtitle ? 2 : 0,
        }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{
            fontFamily: 'Inter', fontWeight: 300,
            fontSize: 'clamp(0.9rem, 1.4vw, 1.05rem)',
            color: 'rgba(255,255,255,0.5)', lineHeight: 1.6,
            maxWidth: 600, mx: 'auto',
          }}>
            {subtitle}
          </Typography>
        )}
      </motion.div>
    </Box>
  );
}

// ─── Feature card ─────────────────────────────────────────────────────────
function FeatureCard({ number, title, body, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.3, once: true });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: (index % 2) * 0.08 }}
      style={{ height: '100%' }}
    >
      <Box sx={{
        height: '100%',
        p: { xs: 3, md: 3.5 },
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 2,
        background: 'rgba(255,255,255,0.015)',
        display: 'flex', flexDirection: 'column', gap: 1.5,
        transition: 'border-color 0.35s ease, background 0.35s ease',
        '&:hover': {
          borderColor: 'rgba(255,255,255,0.22)',
          background: 'rgba(255,255,255,0.03)',
        },
      }}>
        <Typography sx={{
          fontFamily: 'Inter', fontWeight: 500,
          fontSize: '0.7rem', letterSpacing: '0.2em',
          color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase',
          fontVariantNumeric: 'tabular-nums',
        }}>
          {number}
        </Typography>
        <Typography component="h3" sx={{
          fontFamily: 'Inter', fontWeight: 700,
          fontSize: '1.15rem', color: '#fff',
          letterSpacing: '-0.015em', lineHeight: 1.25,
        }}>
          {title}
        </Typography>
        <Typography sx={{
          fontFamily: 'Inter', fontWeight: 300,
          fontSize: '0.9rem', lineHeight: 1.65,
          color: 'rgba(255,255,255,0.55)', flexGrow: 1,
        }}>
          {body}
        </Typography>
      </Box>
    </motion.div>
  );
}

// ─── Scenario block ───────────────────────────────────────────────────────
function ScenarioBlock({ label, headline, body, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.3, once: true });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 16 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: index * 0.08 }}
    >
      <Box sx={{
        p: { xs: 3, md: 4 },
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 2,
        background: 'rgba(255,255,255,0.02)',
      }}>
        <Typography sx={{
          fontFamily: 'Inter', fontWeight: 600,
          fontSize: '0.68rem', letterSpacing: '0.22em',
          color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', mb: 1.5,
        }}>
          {label}
        </Typography>
        <Typography component="h3" sx={{
          fontFamily: 'Inter', fontWeight: 700,
          fontSize: { xs: '1.15rem', md: '1.4rem' },
          color: '#fff', letterSpacing: '-0.015em', mb: 1.5, lineHeight: 1.3,
        }}>
          {headline}
        </Typography>
        <Typography sx={{
          fontFamily: 'Inter', fontWeight: 300,
          fontSize: '0.95rem', lineHeight: 1.7,
          color: 'rgba(255,255,255,0.6)',
        }}>
          {body}
        </Typography>
      </Box>
    </motion.div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────
export default function UseCasePage({
  slug,
  breadcrumbLabel,
  seo,
  eyebrow,
  headline,
  subhead,
  ctaLabel = 'Get in Touch',
  problem,
  features,
  scenarios,
  faqs,
}) {
  const navigate = useNavigate();
  const breadcrumbs = [
    { name: 'Use Cases', url: `https://dublabs.ai/use-cases/${slug}` },
    { name: breadcrumbLabel, url: `https://dublabs.ai/use-cases/${slug}` },
  ];

  useSEO({
    title: seo.title,
    description: seo.description,
    canonical: `https://dublabs.ai/use-cases/${slug}`,
    breadcrumbs,
    faqs: faqs.map(f => ({ question: f.q, answer: f.a })),
    dateModified: '2026-08-04',
  });

  return (
    <Box sx={{ background: '#000', color: '#fff', minHeight: '100vh' }}>
      <Breadcrumbs items={breadcrumbs} />

      {/* Hero */}
      <Box sx={{
        pt: { xs: 6, md: 10 },
        pb: { xs: 8, md: 12 },
        px: { xs: 3, md: 6 },
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Box sx={{
          position: 'absolute', top: -160, left: '50%', transform: 'translateX(-50%)',
          width: 700, height: 700, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.035) 0%, transparent 60%)',
          pointerEvents: 'none',
        }} />
        <Box sx={{ position: 'relative', maxWidth: 820, mx: 'auto' }}>
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            <Typography sx={{
              fontFamily: 'Inter', fontWeight: 500, fontSize: '0.68rem',
              letterSpacing: '0.28em', textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.35)', mb: 2.5,
            }}>
              {eyebrow}
            </Typography>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <Typography component="h1" sx={{
              fontFamily: 'Inter', fontWeight: 900,
              fontSize: 'clamp(2.2rem, 5.5vw, 4rem)',
              lineHeight: 1.02, letterSpacing: '-0.04em', color: '#fff', mb: 2.5,
            }}>
              {headline}
            </Typography>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.32 }}
          >
            <Typography sx={{
              fontFamily: 'Inter', fontWeight: 300,
              fontSize: 'clamp(1rem, 1.6vw, 1.2rem)',
              lineHeight: 1.6, color: 'rgba(255,255,255,0.55)',
              maxWidth: 600, mx: 'auto', mb: 4,
            }}>
              {subhead}
            </Typography>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.44 }}
          >
            <Button
              variant="contained"
              onClick={() => navigate('/contact')}
              endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
              sx={{
                background: '#fff', color: '#000',
                fontFamily: 'Inter', fontWeight: 600, fontSize: '0.9rem',
                textTransform: 'none', px: 3.5, py: 1.25, borderRadius: 1.5,
                letterSpacing: '-0.01em',
                '&:hover': { background: '#f0f0f0' },
              }}
            >
              {ctaLabel}
            </Button>
          </motion.div>
        </Box>
      </Box>

      {/* Problem statement */}
      {problem && (
        <Box sx={{
          maxWidth: 900, mx: 'auto',
          px: { xs: 3, md: 6 },
          pb: { xs: 8, md: 12 },
          textAlign: 'center',
        }}>
          <Typography sx={{
            fontFamily: 'Inter', fontWeight: 400,
            fontSize: 'clamp(1.1rem, 2vw, 1.5rem)',
            lineHeight: 1.6, color: 'rgba(255,255,255,0.75)',
            fontStyle: 'italic',
          }}>
            {problem}
          </Typography>
        </Box>
      )}

      {/* Features */}
      <Box sx={{
        maxWidth: 1200, mx: 'auto',
        px: { xs: 3, md: 6 },
        py: { xs: 8, md: 12 },
      }}>
        <SectionHeading eyebrow="How we help" title="Purpose-built for your workflow" />
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
          gap: { xs: 2, md: 3 },
        }}>
          {features.map((f, i) => (
            <FeatureCard key={f.title} number={f.number} title={f.title} body={f.body} index={i} />
          ))}
        </Box>
      </Box>

      {/* Scenarios */}
      {scenarios && scenarios.length > 0 && (
        <Box sx={{
          maxWidth: 1000, mx: 'auto',
          px: { xs: 3, md: 6 },
          py: { xs: 8, md: 12 },
          borderTop: '1px solid rgba(255,255,255,0.05)',
        }}>
          <SectionHeading eyebrow="Real scenarios" title="What this looks like in practice" />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, md: 3 } }}>
            {scenarios.map((s, i) => (
              <ScenarioBlock key={s.headline} label={s.label} headline={s.headline} body={s.body} index={i} />
            ))}
          </Box>
        </Box>
      )}

      {/* FAQ */}
      {faqs && faqs.length > 0 && (
        <Box sx={{
          maxWidth: 820, mx: 'auto',
          px: { xs: 3, md: 6 },
          py: { xs: 8, md: 12 },
          borderTop: '1px solid rgba(255,255,255,0.05)',
        }}>
          <SectionHeading eyebrow="Questions" title="Frequently asked" />
          <Box>
            {faqs.map((faq) => (
              <Accordion
                key={faq.q}
                sx={{
                  background: 'transparent',
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,0.06)',
                  borderRadius: '10px !important',
                  mb: 1.25,
                  '&:before': { display: 'none' },
                  boxShadow: 'none',
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: 'rgba(255,255,255,0.5)' }} />}
                  sx={{
                    px: { xs: 2.5, md: 3 },
                    '& .MuiAccordionSummary-content': { my: 1.5 },
                  }}
                >
                  <Typography sx={{
                    fontFamily: 'Inter', fontWeight: 500,
                    fontSize: '0.95rem', color: '#fff',
                  }}>
                    {faq.q}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: { xs: 2.5, md: 3 }, pb: 2.5, pt: 0 }}>
                  <Typography sx={{
                    fontFamily: 'Inter', fontWeight: 300,
                    fontSize: '0.9rem', lineHeight: 1.7,
                    color: 'rgba(255,255,255,0.65)',
                  }}>
                    {faq.a}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Box>
      )}

      {/* Final CTA */}
      <Box sx={{
        px: { xs: 3, md: 6 },
        py: { xs: 10, md: 16 },
        textAlign: 'center',
        borderTop: '1px solid rgba(255,255,255,0.05)',
      }}>
        <Typography sx={{
          fontFamily: 'Inter', fontWeight: 800,
          fontSize: 'clamp(1.8rem, 4vw, 3rem)',
          color: '#fff', letterSpacing: '-0.03em', lineHeight: 1.1, mb: 2.5,
        }}>
          Ready to see it work on your video?
        </Typography>
        <Typography sx={{
          fontFamily: 'Inter', fontWeight: 300,
          fontSize: '1rem', color: 'rgba(255,255,255,0.55)',
          maxWidth: 520, mx: 'auto', mb: 4,
        }}>
          Tell us about your project — we'll respond within one business day
          with a quote and next steps.
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/contact')}
          endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
          sx={{
            background: '#fff', color: '#000',
            fontFamily: 'Inter', fontWeight: 600, fontSize: '0.95rem',
            textTransform: 'none', px: 4, py: 1.5, borderRadius: 1.5,
            letterSpacing: '-0.01em',
            '&:hover': { background: '#f0f0f0' },
          }}
        >
          Get in Touch
        </Button>
      </Box>
    </Box>
  );
}
